import { HTMLAttributes, forwardRef } from 'react';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  elevated?: boolean;
  glass?: boolean;
  hoverable?: boolean;
  variant?: 'default' | 'hunter' | 'gatherer';
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({
    elevated = false,
    glass = false,
    hoverable = false,
    variant = 'default',
    children,
    className = '',
    ...props
  }, ref) => {
    const baseClasses = 'rounded-lg transition-all duration-200 ease-in-out';

    // Glass-morphism styling
    const glassClasses = glass
      ? 'bg-white/95 dark:bg-surface-glass backdrop-blur-md border border-slate-200 dark:border-transparent shadow-sm dark:shadow-dark-md'
      : 'bg-surface-elevated dark:bg-surface border border-border dark:border-transparent';

    // Elevation styling
    const elevationClasses = elevated
      ? 'shadow-lg dark:shadow-dark-lg'
      : 'shadow-sm dark:shadow-dark-sm';

    // Hoverable styling
    const hoverClasses = hoverable
      ? 'hover:bg-surface-elevated hover:border-border-hover hover:-translate-y-0.5 hover:shadow-lg dark:hover:shadow-dark-lg cursor-pointer'
      : '';

    // Variant styling
    const variantClasses = {
      default: '',
      hunter: 'border-hunter-500/20 bg-hunter-500/5',
      gatherer: 'border-gatherer-500/20 bg-gatherer-500/5',
    }[variant];

    return (
      <div
        ref={ref}
        className={`
          ${baseClasses}
          ${glassClasses}
          ${elevationClasses}
          ${hoverClasses}
          ${variantClasses}
          p-4
          ${className}
        `}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ children, className = '', ...props }, ref) => {
    return (
      <div ref={ref} className={`mb-3 ${className}`} {...props}>
        {children}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ children, className = '', ...props }, ref) => {
    return (
      <h3 ref={ref} className={`text-lg font-medium text-text-primary ${className}`} {...props}>
        {children}
      </h3>
    );
  }
);

CardTitle.displayName = 'CardTitle';

export const CardDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ children, className = '', ...props }, ref) => {
    return (
      <p ref={ref} className={`text-sm text-text-secondary ${className}`} {...props}>
        {children}
      </p>
    );
  }
);

CardDescription.displayName = 'CardDescription';

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ children, className = '', ...props }, ref) => {
    return (
      <div ref={ref} className={className} {...props}>
        {children}
      </div>
    );
  }
);

CardContent.displayName = 'CardContent';
